// The Day-of-Show hooks — hand-written over the generated client via the tenant-keyed
// wrappers. Keys are ['band', bandId, 'show', showId, …], prefix-children of the show record. All
// MONEY-FREE (the on-phone settlement card is the separate money-gated SettlementPanel). The DOS view rides
// `schedule-changed` (a child of ['show', showId]). Tickets sold are the settlement door headcount (ART-252),
// entered on that card — there is no separate ticket-count log any more.
import { useQueryClient } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { useBandContext } from "../bandContext.js";
import { useBandQuery, useBandMutation } from "./useBandQuery.js";
import { ApiError, errorMessage } from "./apiError.js";
import { pushToast } from "../toasts.js";

type DayOfShowView = components["schemas"]["DayOfShowView"];
type FoodFinderResult = components["schemas"]["FoodFinderResult"];
type DosPdfLink = components["schemas"]["DosPdfLink"];
type AteHereRequest = components["schemas"]["AteHereRequest"];

/** GET the composed on-screen DOS view — setlist/run-of-show/lineup/members/media link + weather.*/
export function useDos(showId: string) {
  const { api } = useApp();
  return useBandQuery<DayOfShowView>(
    ["show", showId, "day-of-show"],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/shows/{showId}/dos", {
        params: { path: { bandId, showId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    // The dashboard heroes call this with "" until their next show resolves — without the guard that fires a
    // malformed `shows//dos` on every mount.
    { staleTime: 30_000, enabled: showId !== "" },
  );
}

/** GET the food finder — Places + community recs, optionally narrowed to a `cuisine` (the sheet's chips).
 * Lazy: pass `enabled` so a Places call fires only when the finder is opened, not on every DOS view. Always
 * resolves (degrades to recs-only when unavailable). The cuisine is part of the key so each chip caches its own. */
export function useDosFood(showId: string, enabled = false, cuisine?: string) {
  const { api } = useApp();
  return useBandQuery<FoodFinderResult>(
    ["show", showId, "food", cuisine ?? ""],
    async (bandId) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/shows/{showId}/dos/food", {
        params: { path: { bandId, showId }, query: cuisine ? { cuisine } : undefined },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { enabled: enabled && !!showId, staleTime: 5 * 60_000 },
  );
}

/** POST "Ate here" — a band-attributed community rec; invalidates the food finder so it resurfaces.*/
export function useAteHere() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, { showId: string; body: AteHereRequest }>(
    async (band, { showId, body }) => {
      const { error, response } = await api.POST("/api/v1/bands/{bandId}/shows/{showId}/dos/food/ate-here", {
        params: { path: { bandId: band, showId } },
        body,
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: (_res, vars) => {
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId, "food"] });
      },
      onError: (err) => pushToast(errorMessage(err, "Couldn't save that rec")),
    },
  );
}

/** PUT the show's shared "Media" link (Drive/Dropbox/WeTransfer), or null to clear it. Invalidates the DOS
 * view so the tile reflects it. schedule:write; the link is band-wide (per show). */
export function useSetMediaLink() {
  const { api } = useApp();
  const { bandId } = useBandContext();
  const qc = useQueryClient();
  return useBandMutation<void, { showId: string; mediaLink: string | null }>(
    async (band, { showId, mediaLink }) => {
      const { error, response } = await api.PUT("/api/v1/bands/{bandId}/shows/{showId}/dos/media", {
        params: { path: { bandId: band, showId } },
        body: { mediaLink },
      });
      if (!response.ok) throw new ApiError(response.status, error);
    },
    {
      onSuccess: (_res, vars) =>
        void qc.invalidateQueries({ queryKey: ["band", bandId, "show", vars.showId, "day-of-show"] }),
      onError: (err) => pushToast(errorMessage(err, "Couldn't save the media link")),
    },
  );
}

/** Mint a fresh signed URL for the printable DOS PDF and hand it back to open in a new tab.*/
export function useDosPdf() {
  const { api } = useApp();
  return useBandMutation<DosPdfLink, { showId: string }>(
    async (bandId, { showId }) => {
      const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/shows/{showId}/dos/pdf", {
        params: { path: { bandId, showId } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    { onError: (err) => pushToast(errorMessage(err, "Couldn't generate the PDF")) },
  );
}
